// Fotomania (feature, canvas soberano A2, Task 14): this is one of TWO permanent independent
// implementations of the SAME protocol. The runtime-side copy lives at
// `studio/src-js/runtime/src/customizer/core/runtime-queue.ts` and resolves the identical
// `Symbol.for('fotomania.runtimeQueue')` key against the same `{enqueue, isInsideOperation}`
// shape. Neither copy is a bridge awaiting an exit; cross-module tests enforce parity.
export const RUNTIME_QUEUE = Symbol.for('fotomania.runtimeQueue');

/** Cross-bundle marker (rev4/rev5): stamped on a queued closure by BOTH bounded-admission
 * implementations (the runtime-side copy's `enqueueBoundedAdmission` in
 * `studio/src-js/runtime/src/customizer/core/runtime-queue.ts` and content-guard.ts's private
 * handshake). This file exports only the queue (`getRuntimeQueue`) and this marker; it has no
 * `enqueueBoundedAdmission` of its own. The 120s starvation watch skips marked closures so an
 * expired bounded admission sitting as a no-op in the tail never double-fires the generic watch. */
export const BOUNDED_ADMISSION_MARKER = Symbol.for('fotomania.boundedAdmission');

/** How long an admission may sit behind one held (unmarked/unbounded) operation before the
 * starvation diagnostic fires. Console-only, never behavioral. */
const STARVATION_WARN_MS = 120_000;

export type RuntimeQueue = {
  enqueue<T>(operation: () => T | Promise<T>): Promise<T>;
  /** True from operation start until its returned promise settles ("the queue is held"). */
  isInsideOperation(): boolean;
};

type MarkedOperation = { [BOUNDED_ADMISSION_MARKER]?: true };

type QueueOwner = Record<PropertyKey, unknown>;

function createQueue(): RuntimeQueue {
  let tail: Promise<unknown> = Promise.resolve();
  let inside = false;

  function runOperation<T>(operation: () => T | Promise<T>): Promise<T> {
    inside = true;
    let started: T | Promise<T>;
    try {
      started = operation();
    } catch (error) {
      inside = false;
      return Promise.reject(error);
    }
    return Promise.resolve(started).finally(() => { inside = false; });
  }

  return {
    enqueue<T>(operation: () => T | Promise<T>): Promise<T> {
      const marked = (operation as MarkedOperation)[BOUNDED_ADMISSION_MARKER] === true;
      let started = false;

      if (!marked) {
        // Fires once if THIS admission has still not started (i.e. is sitting behind a held
        // operation) once the starvation threshold elapses. An admission that has already
        // started its own long-running work by then never warns about itself.
        setTimeout(() => {
          if (started) return;
          // eslint-disable-next-line no-console
          console.warn('[Fotomania] runtime-queue: an admission has waited over 120s behind one held operation');
        }, STARVATION_WARN_MS);
      }

      const result = tail.then(
        () => { started = true; return runOperation(operation); },
        () => { started = true; return runOperation(operation); },
      );
      tail = result.then(() => undefined, () => undefined);
      return result;
    },
    isInsideOperation(): boolean {
      return inside;
    },
  };
}

export function getRuntimeQueue(runtime: object): RuntimeQueue {
  const owner = runtime as QueueOwner;
  const existing = owner[RUNTIME_QUEUE] as RuntimeQueue | undefined;
  if (existing?.enqueue) return existing;

  const queue = createQueue();
  Object.defineProperty(owner, RUNTIME_QUEUE, {
    configurable: false,
    enumerable: false,
    writable: false,
    value: queue,
  });
  return queue;
}
